// src/services/trashService.ts

import { apiClient } from "./apiClient";
import { PageResponse } from "../models/PaginationModel";

// Lightweight DTO for entries sitting in the user's personal trash
export interface TrashEntry {
  id: number;
  content: string;
  deletedAt: string; // ISO date-time string

  // Match topic fields — null for non-match entries
  matchId: number | null;
  homeTeam: string | null;
  awayTeam: string | null;
  matchDate: string | null; // ISO date string (YYYY-MM-DD)

  // Person topic fields — null for non-person entries
  personId: number | null;
  personFullName: string | null;
  personSlug: string | null;

  // Club topic fields — null for non-club entries
  clubId: number | null;
  clubName: string | null;
  clubSlug: string | null;

  // Team topic fields — null for non-team entries
  teamId: number | null;
  teamName: string | null;
  teamClubSlug: string | null; // parent club slug — first URL segment in /takim/{clubSlug}/{teamSlug}
  teamSlug: string | null;     // sport+gender+teamCode slug — second URL segment
  teamCode: string | null;     // sub-team code for display label (e.g. "u19", "1b")

  // Custom topic ("gündem") fields — null for non-custom-topic entries
  customTopicId: number | null;
  customTopicTitle: string | null;
  customTopicSlug: string | null;
}

/**
 * Fetch a page of the current user's self-deleted (trashed) entries.
 * Entries removed by admins/mods are not included.
 */
export const getTrashEntries = async (
  page: number,
  size: number
): Promise<PageResponse<TrashEntry>> => {
  return apiClient.get<PageResponse<TrashEntry>>(
    `/api/entries/trash?page=${page}&size=${size}`
  );
};

/**
 * Permanently hard-delete a single entry from the user's trash.
 * This action is irreversible.
 */
export const hardDeleteFromTrash = async (entryId: number): Promise<void> => {
  return apiClient.delete<void>(`/api/entries/trash/${entryId}`);
};

/**
 * Hard-delete ALL entries in the user's trash — empty the bin.
 * This action is irreversible.
 */
export const emptyTrash = async (): Promise<void> => {
  return apiClient.delete<void>(`/api/entries/trash`);
};